import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { checkersApi } from "../../api/checkersApi";
import CheckersBoard from "./CheckersBoard";
import CheckersStatusBar from "./CheckersStatusBar";
import CheckersResult from "./CheckersResult";
import { useCheckersGame } from "./hooks/useCheckersGame";
import { showToast } from "../../utils/toast/Toast";
import GameBoardFrame, { useGameBoardFrameMetrics } from "../games/GameBoardFrame";

export default function CheckersAIPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [gameId, setGameId] = useState(params.id || null);
  const { state, move } = useCheckersGame(gameId, { multiplayer: false });
  const { game, myPiece, status, error } = state;
  const { isDesktop } = useGameBoardFrameMetrics();
  const boardMaxWidthStyle = "min(88vw, 640px, calc(100dvh - 260px))";

  const createInitial = useCallback(async () => {
    try {
      const game = await checkersApi.createGame(true);
      setGameId(String(game.id));
      navigate(`/games/checkers/ai/${game.id}`, { replace: true });
    } catch {
      showToast("error", "Could not create checkers game.");
    }
  }, [navigate]);

  useEffect(() => {
    if (!gameId) createInitial();
  }, [gameId, createInitial]);

  const handlePlayAgain = useCallback(async () => {
    try {
      const game = await checkersApi.createGame(true);
      setGameId(String(game.id));
      navigate(`/games/checkers/ai/${game.id}`, { replace: true });
    } catch {
      showToast("error", "Could not create checkers rematch.");
    }
  }, [navigate]);

  return (
    <GameBoardFrame>
      {status === "loading" && <div className="text-text-secondary text-sm py-8">Loading game...</div>}
      {status === "error" && <div className="text-brand-rose text-sm py-4">{error}</div>}

      {game && status !== "error" ? (
        <>
          <div className="w-full flex flex-col items-center">
            <div className="w-full">
              <CheckersStatusBar
                currentTurn={game.current_turn}
                myPiece={myPiece}
                winner={game.winner}
                isCompleted={game.is_completed}
                p1Name="You"
                p2Name="AI"
                isAI
                maxWidthStyle={boardMaxWidthStyle}
              />
            </div>
            <CheckersBoard
              board={game.board}
              legalMoves={game.legal_moves}
              myPiece={myPiece}
              currentTurn={game.current_turn}
              isGameOver={game.is_completed}
              onMove={move}
              maxWidthStyle={boardMaxWidthStyle}
            />
          </div>
          <div className="w-full flex items-start justify-center">
            {isDesktop ? (
              <div className="w-full min-h-[76px] sm:min-h-[96px] flex items-start justify-center">
                <CheckersResult
                  isCompleted={game.is_completed}
                  winner={game.winner}
                  myPiece={myPiece}
                  onPlayAgain={handlePlayAgain}
                />
              </div>
            ) : (
              // Mobile only: no reserved slot while the game is in progress -- a
              // permanently blank min-h box below the board reads as dead space.
              // Animate it open only once the game ends so the card eases into place
              // instead of popping into a gap that's been sitting empty the whole game.
              <AnimatePresence initial={false}>
                {game.is_completed && (
                  <motion.div
                    className="w-full overflow-hidden flex items-start justify-center"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <CheckersResult
                      isCompleted={game.is_completed}
                      winner={game.winner}
                      myPiece={myPiece}
                      onPlayAgain={handlePlayAgain}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </>
      ) : null}
    </GameBoardFrame>
  );
}
